import * as React from "react";
import PdfPrinter from "pdfmake";
import { useEffect, useState } from "react";
import { useOrganization } from "../configurations/OrganizationContext";
import { PRINT_FILE, PRINT_PREVIEW } from "../shared/constants";
import { convertDocDefinitionToHTML } from "../utils/docDefToHtml";
var fonts = require("pdfmake/build/vfs_fonts");

function RadiologyPrint(props) {
  console.log("props in radiology print", props);
  const org = useOrganization();
  const [patientInfo] = useState(props.selectedPatient);
  const [doctorDetails] = useState(props.selectedPatient.person);
  let location = "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printPreviewType] = useState(props.printPreviewType);

  useEffect(() => {
    const generateReports = async () => {
      if (
        props.selectedPatient?.type === "InPatient" &&
        props.selectedPatient.room?.roomName
      ) {
        location = props.selectedPatient.room.roomName;
      } else {
        location = "opd";
      }

      try {
        if (!props.prescriptionList?.length) {
          throw new Error("No reports selected for printing");
        }

        const reportDefinitions = await Promise.all(
          props.prescriptionList.map(async (procedure) => {
            const reportContent = procedure.longdesc || procedure.notes || "";
            const now = new Date();

            const templateType =
              procedure.templateId || procedure.packageService?.name;
            let getDocDefinition;

            switch (templateType) {
              case "ABDOMINAL/PELVIS SONOGRAPHY":
                getDocDefinition = (
                  await import("../mnh/docdefradiologyabdominal")
                ).getDocDefinition;
                break;
              case "OBSTETRIC ULTRA SONOGRAPHY":
                getDocDefinition = (
                  await import("../mnh/docdefradiologyobstetric")
                ).getDocDefinition;
                break;
              case "FOLLICULAR STUDY CYCLE":
                getDocDefinition = (
                  await import("../mnh/docdefradiologyfollicular")
                ).getDocDefinition;
                break;
              case "SCROTUM":
                getDocDefinition = (
                  await import("../mnh/docdefradiologyabdominal")
                ).getDocDefinition;
                break;
              case "DOPPLER  STUDY":
              case "DOPPLER STUDY":
                getDocDefinition = (
                  await import("../mnh/docdefradiologydoppler")
                ).getDocDefinition;
                break;
              case "LOWER LIMB VENOUS SYSTEM":
                getDocDefinition = (
                  await import("../mnh/docdefradiologylowerlimb")
                ).getDocDefinition;
                break;
              case "LOWER LIMB ARTERIAL SYSTEM":
                getDocDefinition = (
                  await import("../mnh/docdefradiologylowerlimb")
                ).getDocDefinition;
                break;
              case "RADIOLOGY OTHER":
                getDocDefinition = (
                  await import("../mnh/docdefradiologyabdominal")
                ).getDocDefinition;
                break;
              default:
                getDocDefinition = (
                  await import("../mnh/docdefradiologyfollicular")
                ).getDocDefinition;
            }

            const reportData = {
              patientInfo: props.selectedPatient,
              location: location,
              doctorDetails: doctorDetails,
              reportDate: now.toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
              }),
              reportTime: now.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              }),
              reportContent: reportContent,
              title: getReportTitle(procedure),
              procedureData: procedure,
              templateType: templateType,
            };

            return getDocDefinition(reportData, org);
          })
        );

        const combinedDefinition = {
          pageSize: "A4",
          pageMargins: [40, 60, 40, 60],
          content: reportDefinitions.flatMap((def, index) => [
            ...(index > 0 ? [
              {
                stack: [
                  { text: "\u200B", margin: [0, 100] },
                  {
                    canvas: [
                      {
                        type: 'line',
                        x1: 0, y1: 0,
                        x2: 515, y2: 0,
                        lineWidth: 0.5,
                        lineColor: '#ccc'
                      }
                    ],
                    height: 1
                  },
                  { text: "\u200B", margin: [0, 60] }
                ]
              }
            ] : []),
            {
              stack: def.content,
              margin: [0, 20, 0, 20]
            }
          ]),
          styles: reportDefinitions[0]?.styles || {},
          defaultStyle: reportDefinitions[0]?.defaultStyle || {
            font: "Helvetica",
          },
        };

        // ✅ PRINT_PREVIEW → Use PDFMake (native PDF viewer)
        if (printPreviewType === PRINT_PREVIEW) {
          PdfPrinter.createPdf(combinedDefinition, null, null, fonts.pdfMake.vfs).open();
        }
        // ✅ PRINT_FILE → Use HTML + iframe (print in same tab)
        else if (printPreviewType === PRINT_FILE) {
          const htmlContent = convertDocDefinitionToHTML(combinedDefinition, {
            allowScaling: true,
          });

          // Create hidden iframe
          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed';
          iframe.style.width = '0';
          iframe.style.height = '0';
          iframe.style.border = 'none';
          iframe.style.top = '0';
          iframe.style.left = '0';
          iframe.style.zIndex = '-1';
          iframe.setAttribute('aria-hidden', 'true');
          iframe.setAttribute('tabindex', '-1');

          document.body.appendChild(iframe);

          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          iframeDoc.open();
          iframeDoc.write(htmlContent);
          iframeDoc.close();

          let hasPrinted = false;

          const triggerPrint = () => {
            if (hasPrinted) return;
            hasPrinted = true;

            const iframeWin = iframe.contentWindow;
            iframeWin.focus();
            iframeWin.print();

            const removeIframe = () => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
              }
            };

            if ('onafterprint' in iframeWin) {
              iframeWin.onafterprint = removeIframe;
            } else {
              setTimeout(removeIframe, 3000);
            }
          };

          iframe.onload = () => {
            setTimeout(triggerPrint, 500);
          };

          setTimeout(() => {
            if (!hasPrinted && document.body.contains(iframe)) {
              triggerPrint();
            }
          }, 2000);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error generating PDF:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    generateReports();
  }, [props.prescriptionList, printPreviewType]);

  const getReportTitle = (procedure) => {
    const titles = {
      "ABDOMINAL/PELVIS SONOGRAPHY": "ABDOMINAL/PELVIS SONOGRAPHY REPORT",
      "OBSTETRIC ULTRA SONOGRAPHY": "OBSTETRIC ULTRASOUND REPORT",
      "FOLLICULAR STUDY CYCLE": "FOLLICULAR STUDY REPORT",
      SCROTUM: "SCROTUM",
      "LOWER LIMB VENOUS SYSTEM": "LOWER LIMB VENOUS SYSTEM REPORT",
      "LOWER LIMB ARTERIAL SYSTEM": "LOWER LIMB ARTERIAL SYSTEM ",
      "DOPPLER  STUDY": "DOPPLER STUDY REPORT",
      "RADIOLOGY OTHER": "RADIOLOGY OTHER REPORT",
    };

    return (
      titles[procedure.templateId] ||
      titles[procedure.packageService?.name] ||
      procedure.templateId ||
      procedure.packageService?.name ||
      "RADIOLOGY REPORT"
    );
  };

  if (loading) return <div>Preparing reports...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return <div style={{ display: "none" }}>Report generation in progress...</div>;
}

export default RadiologyPrint;